import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Plus, Search, User, Dumbbell, Pencil, Users, Trash2, AlertTriangle } from "lucide-react"
import { getClients, createClient, updateClient, deleteClient } from "../../api/clients"
import { createMembership, updateMembership } from "../../api/memberships"
import type { Client } from "../../types"

function formatCRC(amount: number) {
  return new Intl.NumberFormat("es-CR", { style: "currency", currency: "CRC", minimumFractionDigits: 0 }).format(amount)
}

const PLANS = [
  { value: "bronze",            label: "1 sesión/semana",                sublabel: "Clase Grupal", price: 29900, color: "text-amber-400",  activeBg: "bg-amber-400/10 border-amber-400" },
  { value: "pro",               label: "2 sesiones/semana",              sublabel: "Clase Grupal", price: 47900, color: "text-primary",     activeBg: "bg-primary/10 border-primary" },
  { value: "gold",              label: "3 sesiones/semana",              sublabel: "Clase Grupal", price: 65900, color: "text-yellow-400",  activeBg: "bg-yellow-400/10 border-yellow-400" },
  { value: "platinum",          label: "4 sesiones/semana",              sublabel: "Clase Grupal", price: 75900, color: "text-purple-400",  activeBg: "bg-purple-400/10 border-purple-400" },
  { value: "elite",             label: "5 sesiones/semana",              sublabel: "Clase Grupal", price: 99900, color: "text-emerald-400", activeBg: "bg-emerald-400/10 border-emerald-400" },
  { value: "personal_training", label: "Entrenamiento Personal",         sublabel: "por cita",     price: 15000, color: "text-blue-400",    activeBg: "bg-blue-400/10 border-blue-400" },
  { value: "virtual",           label: "Asesoría/Entrenamiento Virtual", sublabel: "mensual",      price: 35000, color: "text-cyan-400",    activeBg: "bg-cyan-400/10 border-cyan-400" },
  { value: "regular",           label: "Personalizado",                  sublabel: "sin plan",     price: 0,     color: "text-slate-300",   activeBg: "bg-slate-400/10 border-slate-400" },
]

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
}

// ── Modal Agregar Cliente ──────────────────────────────────────────────────
function AddClientModal({ onClose, onAdded }: { onClose: () => void; onAdded: (c: Client) => void }) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [clientType, setClientType] = useState<"personal" | "group_class">("personal")
  const [plan, setPlan] = useState("bronze")
  const [numCitas, setNumCitas] = useState(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectedPlan = PLANS.find((p) => p.value === plan)!
  const totalPrice = plan === "personal_training" ? selectedPlan.price * numCitas : selectedPlan.price

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !email || !password) return
    setSaving(true)
    setError(null)
    try {
      const client = await createClient({ name, email, password, client_type: clientType })
      const today = new Date()
      const endDate = new Date(today)
      endDate.setFullYear(endDate.getFullYear() + 1)
      await createMembership({
        user_id: client.id,
        plan,
        price: totalPrice,
        status: "active",
        start_date: today.toISOString().split("T")[0],
        end_date: endDate.toISOString().split("T")[0],
      })
      onAdded(client)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0]
      setError(msg ?? "Error al crear el cliente.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-4 w-full max-w-md rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Nuevo Cliente</h2>
            <p className="text-xs text-muted">Ingresa los datos del cliente</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white transition text-xl">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Nombre</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Juan Pérez"
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="juan@ejemplo.com"
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Contraseña Temporal</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="mín. 8 caracteres"
              required
              minLength={8}
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">Tipo de Cliente</label>
            <div className="flex gap-2">
              {(["personal", "group_class"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setClientType(t)}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm transition border ${
                    clientType === t
                      ? "bg-primary/10 border-primary text-primary"
                      : "border-white/10 text-muted hover:text-white hover:border-white/20"
                  }`}
                >
                  {t === "personal" ? <User size={14} /> : <Users size={14} />}
                  {t === "personal" ? "Personal" : "Clase Grupal"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">Plan de Membresía</label>
            <div className="space-y-2">
              <p className="text-xs text-muted">Clases Grupales <span className="opacity-60">(precio mensual)</span></p>
              {PLANS.filter((p) => ["bronze","pro","gold","platinum","elite"].includes(p.value)).map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPlan(p.value)}
                  className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                    plan === p.value
                      ? `${p.activeBg} ${p.color}`
                      : "border-white/10 text-muted hover:text-white hover:border-white/20"
                  }`}
                >
                  <span className="font-medium">{p.label}</span>
                  <span className={plan === p.value ? p.color : "text-muted"}>{formatCRC(p.price)}</span>
                </button>
              ))}
              <div className="border-t border-white/10 pt-2 space-y-2">
                {PLANS.filter((p) => ["personal_training","virtual"].includes(p.value)).map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => { setPlan(p.value); setNumCitas(1) }}
                    className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                      plan === p.value
                        ? `${p.activeBg} ${p.color}`
                        : "border-white/10 text-muted hover:text-white hover:border-white/20"
                    }`}
                  >
                    <span className="font-medium">{p.label} <span className="text-xs opacity-60">{p.sublabel}</span></span>
                    <span className={plan === p.value ? p.color : "text-muted"}>
                      {formatCRC(p.price)}{p.value === "personal_training" ? "/cita" : ""}
                    </span>
                  </button>
                ))}
              </div>
              {plan === "personal_training" && (
                <div className="flex items-center gap-3 rounded-lg border border-blue-400/30 bg-blue-400/5 px-4 py-3">
                  <span className="text-sm text-muted">Número de citas:</span>
                  <input
                    type="number"
                    min={1}
                    value={numCitas}
                    onChange={(e) => setNumCitas(Math.max(1, Number(e.target.value)))}
                    className="w-20 rounded-lg border border-white/10 bg-surface-elevated px-2 py-1.5 text-sm text-white text-center focus:border-primary"
                  />
                  <span className="ml-auto text-sm font-semibold text-blue-400">Total: {formatCRC(totalPrice)}</span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <p className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">{error}</p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan disabled:opacity-50"
            >
              {saving ? "Creando..." : "Crear Cliente"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// ── Modal Editar Cliente ───────────────────────────────────────────────────
function EditClientModal({
  client,
  onClose,
  onUpdated,
}: {
  client: Client
  onClose: () => void
  onUpdated: (c: Client) => void
}) {
  const [name, setName] = useState(client.name)
  const [email, setEmail] = useState(client.email)
  const [clientType, setClientType] = useState<"personal" | "group_class">(client.client_type ?? "personal")
  const [plan, setPlan] = useState(client.membership?.plan ?? "bronze")
  const [numCitas, setNumCitas] = useState(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectedPlan = PLANS.find((p) => p.value === plan)!
  const totalPrice = plan === "personal_training" ? selectedPlan.price * numCitas : selectedPlan.price

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const updated = await updateClient(client.id, { name, email, client_type: clientType })
      let updatedMembership = client.membership
      if (client.membership) {
        updatedMembership = await updateMembership(client.membership.id, {
          plan,
          // El plan "regular" tiene precio libre (se define en CRM):
          // preservarlo en vez de pisarlo con el precio 0 del catálogo.
          price: plan === "regular" ? client.membership.price : totalPrice,
        })
      }
      onUpdated({ ...updated, membership: updatedMembership })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0]
      setError(msg ?? "Error al actualizar el cliente.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mx-4 w-full max-w-md rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Editar Cliente</h2>
            <p className="text-xs text-muted">{client.name}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white transition text-xl">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Nombre</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted uppercase tracking-wide">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">Tipo</label>
            <div className="flex gap-2">
              {(["personal", "group_class"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setClientType(t)}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm transition border ${
                    clientType === t
                      ? "bg-primary/10 border-primary text-primary"
                      : "border-white/10 text-muted hover:text-white"
                  }`}
                >
                  {t === "personal" ? "Personal" : "Clase Grupal"}
                </button>
              ))}
            </div>
          </div>

          {client.membership && (
            <div>
              <label className="mb-2 block text-xs font-medium text-muted uppercase tracking-wide">Plan de Membresía</label>
              <div className="space-y-2">
                <p className="text-xs text-muted">Clases Grupales <span className="opacity-60">(precio mensual)</span></p>
                {PLANS.filter((p) => ["bronze","pro","gold","platinum","elite"].includes(p.value)).map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPlan(p.value)}
                    className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                      plan === p.value
                        ? `${p.activeBg} ${p.color}`
                        : "border-white/10 text-muted hover:text-white hover:border-white/20"
                    }`}
                  >
                    <span className="font-medium">{p.label}</span>
                    <span className={plan === p.value ? p.color : "text-muted"}>{formatCRC(p.price)}</span>
                  </button>
                ))}
                <div className="border-t border-white/10 pt-2 space-y-2">
                  {PLANS.filter((p) => ["personal_training","virtual"].includes(p.value)).map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => { setPlan(p.value); setNumCitas(1) }}
                      className={`w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm transition border ${
                        plan === p.value
                          ? `${p.activeBg} ${p.color}`
                          : "border-white/10 text-muted hover:text-white hover:border-white/20"
                      }`}
                    >
                      <span className="font-medium">{p.label} <span className="text-xs opacity-60">{p.sublabel}</span></span>
                      <span className={plan === p.value ? p.color : "text-muted"}>
                        {formatCRC(p.price)}{p.value === "personal_training" ? "/cita" : ""}
                      </span>
                    </button>
                  ))}
                </div>
                {plan === "personal_training" && (
                  <div className="flex items-center gap-3 rounded-lg border border-blue-400/30 bg-blue-400/5 px-4 py-3">
                    <span className="text-sm text-muted">Número de citas:</span>
                    <input
                      type="number"
                      min={1}
                      value={numCitas}
                      onChange={(e) => setNumCitas(Math.max(1, Number(e.target.value)))}
                      className="w-20 rounded-lg border border-white/10 bg-surface-elevated px-2 py-1.5 text-sm text-white text-center focus:border-primary"
                    />
                    <span className="ml-auto text-sm font-semibold text-blue-400">Total: {formatCRC(totalPrice)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <p className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">{error}</p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar Cambios"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

// ── Modal Eliminar Cliente ─────────────────────────────────────────────────
function DeleteClientModal({
  client,
  onClose,
  onDeleted,
}: {
  client: Client
  onClose: () => void
  onDeleted: () => void
}) {
  const [confirm, setConfirm] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isConfirmed = confirm.trim().toLowerCase() === client.name.trim().toLowerCase()

  async function handleDelete() {
    if (!isConfirmed) return
    setDeleting(true)
    setError(null)
    try {
      await deleteClient(client.id)
      onDeleted()
    } catch {
      setError("No se pudo eliminar el cliente. Intentá de nuevo.")
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl border border-danger/30 gradient-card p-6 shadow-2xl"
      >
        {/* Header */}
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger/10 border border-danger/20">
            <AlertTriangle size={18} className="text-danger" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Eliminar cliente</h2>
            <p className="text-xs text-muted mt-0.5">
              Esta acción es irreversible. Se eliminarán todas las rutinas, pagos y datos asociados a <span className="text-white font-medium">{client.name}</span>.
            </p>
          </div>
          <button onClick={onClose} className="ml-auto text-muted hover:text-white transition text-xl shrink-0">
            &times;
          </button>
        </div>

        {/* Confirmación por nombre */}
        <div className="space-y-3">
          <p className="text-xs text-muted">
            Escribí el nombre del cliente para confirmar:
            <span className="ml-1 font-semibold text-white">{client.name}</span>
          </p>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Nombre del cliente"
            className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-danger"
            autoFocus
          />
          {error && (
            <p className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">{error}</p>
          )}
        </div>

        {/* Acciones */}
        <div className="mt-5 flex gap-2">
          <button
            onClick={handleDelete}
            disabled={!isConfirmed || deleting}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-danger py-2.5 text-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-40"
          >
            <Trash2 size={14} />
            {deleting ? "Eliminando..." : "Eliminar cliente"}
          </button>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
          >
            Cancelar
          </button>
        </div>
      </motion.div>
    </div>
  )
}

// ── Main Training Tab ──────────────────────────────────────────────────────
export default function TrainingTab() {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [deletingClient, setDeletingClient] = useState<Client | null>(null)
  const [search, setSearch] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    getClients()
      .then((data) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]))
      .finally(() => setLoading(false))
  }, [])

  const filtered = clients.filter(
    (c) =>
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Dumbbell size={40} className="mx-auto mb-3 text-primary animate-pulse-glow" />
          <p className="text-muted text-sm">Cargando clientes...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {showAdd && (
        <AddClientModal
          onClose={() => setShowAdd(false)}
          onAdded={(c) => {
            setClients((prev) => [...prev, c])
            setShowAdd(false)
          }}
        />
      )}

      {editingClient && (
        <EditClientModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onUpdated={(updated) => {
            setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
            setEditingClient(null)
          }}
        />
      )}

      {deletingClient && (
        <DeleteClientModal
          client={deletingClient}
          onClose={() => setDeletingClient(null)}
          onDeleted={() => {
            setClients((prev) => prev.filter((c) => c.id !== deletingClient.id))
            setDeletingClient(null)
          }}
        />
      )}

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start gap-3">
        <div>
          <h2
            className="text-sm font-semibold text-white uppercase tracking-widest"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Gestión de Clientes
          </h2>
          <p className="text-xs text-muted">{clients.length} clientes registrados</p>
        </div>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="rounded-lg border border-white/10 bg-surface-elevated pl-9 pr-3 py-2 text-sm text-white focus:border-primary w-36 sm:w-48"
            />
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 sm:px-4 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Nuevo </span>Cliente
          </button>
        </div>
      </div>

      {/* Client Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((client, i) => {
          const planInfo = PLANS.find((p) => p.value === client.membership?.plan)
          return (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="gradient-card border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors group"
            >
              {/* Avatar + Name */}
              <div className="mb-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                  {getInitials(client.name)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate">{client.name}</p>
                  <p className="text-xs text-muted truncate">{client.email}</p>
                </div>
              </div>

              {/* Membership Badge */}
              <div className="mb-4 flex items-center gap-2">
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    client.client_type === "personal"
                      ? "bg-primary/10 text-primary"
                      : "bg-primary/5 text-primary/70"
                  }`}
                >
                  {client.client_type === "personal" ? "Personal" : "Clase Grupal"}
                </span>
                {client.membership && (
                  <span className={`text-xs font-medium ${planInfo?.color ?? "text-muted"}`}>
                    {client.membership.plan}
                  </span>
                )}
                {client.membership && (
                  <span
                    className={`ml-auto rounded-full px-2 py-0.5 text-xs ${
                      client.membership.status === "active"
                        ? "bg-success/20 text-success"
                        : "bg-warning/20 text-warning"
                    }`}
                  >
                    {client.membership.status === "active" ? "Activo" : "Suspendido"}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/dashboard/clients/${client.id}`)}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-white/10 py-2 text-xs text-muted hover:text-white hover:border-primary transition"
                >
                  <User size={12} />
                  Perfil
                </button>
                <button
                  onClick={() => navigate(`/dashboard/programs/${client.id}`)}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-white/10 py-2 text-xs text-muted hover:text-primary hover:border-primary transition"
                >
                  <Dumbbell size={12} />
                  Programa
                </button>
                <button
                  onClick={() => setEditingClient(client)}
                  className="rounded-lg border border-white/10 px-3 py-2 text-muted hover:text-white hover:border-white/30 transition"
                  title="Editar cliente"
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={() => setDeletingClient(client)}
                  className="rounded-lg border border-white/10 px-3 py-2 text-muted hover:text-danger hover:border-danger/40 transition"
                  title="Eliminar cliente"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </motion.div>
          )
        })}
      </div>

      {filtered.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <Users size={48} className="mb-4 text-muted opacity-30" />
          <p className="text-muted text-sm">
            {search ? "No se encontraron clientes con ese nombre." : "Aún no hay clientes. ¡Agrega el primero!"}
          </p>
          {!search && (
            <button
              onClick={() => setShowAdd(true)}
              className="mt-4 flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/30 px-4 py-2 text-sm text-primary hover:bg-primary/20 transition"
            >
              <Plus size={14} />
              Agregar Cliente
            </button>
          )}
        </motion.div>
      )}
    </div>
  )
}
